'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  createSelectListCollection,
  Field,
  Input,
  RadioButtonGroup,
  Select,
  SettingsEditDialog,
} from '@terra/ui-design-system';
import { ArrowDown01Icon, Tick01Icon } from '@terra/ui-icons';
import {
  AiApplicationMcpTransportSchema,
  type AiApplicationMcpTransport,
} from '@terra/shared-contracts-ai-applications';
import type {
  McpAuthMode,
  McpServerResponseDto,
  UpsertMcpCredentialDto,
} from '@terra/shared-contracts-mcp-servers';
import { AiApplicationMcpCredentialFields } from './AiApplicationMcpCredentialFields';

type McpConnectionEditDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void;
  isSaving: boolean;
  canEditUrl: boolean;
  hasManagedIdentities: boolean;
  isOAuthOnlyEdit: boolean;
  transport: AiApplicationMcpTransport;
  onTransportChange: (transport: AiApplicationMcpTransport) => void;
  url: string;
  urlError: string;
  onUrlChange: (url: string) => void;
  authMode: McpAuthMode;
  onAuthModeChange: (mode: McpAuthMode) => void;
  credentialLabel: string;
  onCredentialLabelChange: (label: string) => void;
  mcpServerId: string | null;
  existingServer: McpServerResponseDto | null | undefined;
  onCredentialChange: (credential: UpsertMcpCredentialDto | null) => void;
};

export function McpConnectionEditDialog({
  open,
  onOpenChange,
  onSubmit,
  isSaving,
  canEditUrl,
  hasManagedIdentities,
  isOAuthOnlyEdit,
  transport,
  onTransportChange,
  url,
  urlError,
  onUrlChange,
  authMode,
  onAuthModeChange,
  credentialLabel,
  onCredentialLabelChange,
  mcpServerId,
  existingServer,
  onCredentialChange,
}: McpConnectionEditDialogProps) {
  const tAi = useTranslations('aiApplications');
  const tAiSettings = useTranslations('aiApplications.settings');
  const tCommon = useTranslations('common');
  const transportCollection = useMemo(
    () =>
      createSelectListCollection({
        items: AiApplicationMcpTransportSchema.options.map((item) => ({
          label: tAi(`transportOptions.${item}`),
          value: item,
        })),
      }),
    [tAi],
  );

  return (
    <SettingsEditDialog
      open={open}
      onOpenChange={onOpenChange}
      title={tAiSettings('edit.mcpConnection')}
      cancelText={tCommon('cancel')}
      submitText={tCommon('saveChanges')}
      loading={isSaving}
      isSubmitDisabled={canEditUrl ? !url.trim() || Boolean(urlError) : false}
      onSubmit={onSubmit}
      contentMaxHeight="70vh"
    >
      <VStack alignItems="stretch" gap={4}>
        <Field.Root required>
          <Field.Label>{tAi('fields.mcpTransport')}</Field.Label>
          <Select.Root
            collection={transportCollection}
            value={[transport]}
            onValueChange={({ value }) => {
              const parsed = AiApplicationMcpTransportSchema.safeParse(
                value[0],
              );

              if (parsed.success) {
                onTransportChange(parsed.data);
              }
            }}
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText />
                <ArrowDown01Icon aria-hidden="true" />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                {transportCollection.items.map((item) => (
                  <Select.Item key={item.value} item={item}>
                    <Select.ItemText>{item.label}</Select.ItemText>
                    <Select.ItemIndicator>
                      <Tick01Icon aria-hidden="true" />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Field.Root>
        <Field.Root required invalid={Boolean(urlError)}>
          <Field.Label>{tAi('fields.mcpUrl')}</Field.Label>
          <Input
            value={url}
            onChange={(event) => onUrlChange(event.target.value)}
            placeholder={tAi('fields.mcpUrlPlaceholder')}
            disabled={!canEditUrl}
          />
          {urlError ? <Field.ErrorText>{urlError}</Field.ErrorText> : null}
        </Field.Root>
        {hasManagedIdentities ? (
          <BodyDefault color="fg.muted">
            {tAiSettings('mcpConnection.identityOAuthHint')}
          </BodyDefault>
        ) : (
          <>
            {!isOAuthOnlyEdit ? (
              <Field.Root w="full">
                <Field.Label>
                  {tAiSettings('mcpConnection.authModeLabel')}
                </Field.Label>
                <RadioButtonGroup.Root
                  value={authMode}
                  onValueChange={(details) => {
                    if (
                      details.value === 'oauth' ||
                      details.value === 'manualCredentials'
                    ) {
                      onAuthModeChange(details.value);
                    }
                  }}
                >
                  <RadioButtonGroup.Item value="oauth">
                    <RadioButtonGroup.ItemControl />
                    <RadioButtonGroup.ItemText>
                      {tAiSettings('mcpConnection.authModeOAuth')}
                    </RadioButtonGroup.ItemText>
                    <RadioButtonGroup.ItemHiddenInput />
                  </RadioButtonGroup.Item>
                  <RadioButtonGroup.Item value="manualCredentials">
                    <RadioButtonGroup.ItemControl />
                    <RadioButtonGroup.ItemText>
                      {tAiSettings('mcpConnection.authModeManualCredentials')}
                    </RadioButtonGroup.ItemText>
                    <RadioButtonGroup.ItemHiddenInput />
                  </RadioButtonGroup.Item>
                </RadioButtonGroup.Root>
              </Field.Root>
            ) : null}
            {authMode === 'oauth' ? (
              <>
                <Field.Root>
                  <Field.Label>
                    {tAi('mcpIdentities.identityLabel')}
                  </Field.Label>
                  <Input
                    value={credentialLabel}
                    onChange={(event) =>
                      onCredentialLabelChange(event.target.value)
                    }
                    placeholder={tAi('mcpIdentities.defaultBadge')}
                  />
                </Field.Root>
                <AiApplicationMcpCredentialFields
                  mcpServerId={mcpServerId}
                  existingServer={existingServer}
                  discoveryUrl={url}
                  onCredentialChange={onCredentialChange}
                  enableOAuthAuthorizeButton={false}
                  showNoneConfirmButton={false}
                  credentialTypeSeed={isOAuthOnlyEdit ? 'OAUTH_DCR' : undefined}
                  hideAuthTypeSelector={isOAuthOnlyEdit}
                />
              </>
            ) : (
              <BodyDefault color="fg.muted">
                {tAiSettings('mcpConnection.manualCredentialsNotice')}
              </BodyDefault>
            )}
          </>
        )}
      </VStack>
    </SettingsEditDialog>
  );
}
